import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import React, { useEffect } from 'react';
import { bindActionCreators } from 'redux';
import { Nav, NavItem } from 'react-bootstrap/lib';
import { LinkContainer } from 'react-router-bootstrap/lib';
import { UserRole } from '@upstart/harmony/lib/constants';
import SidebarContainer from '../SidebarContainer';
import websitesActions from '../../redux/websites/actions';
import feedviewsActions from '../../redux/feedviews/actions';
import { getWebsite } from '../../redux/websites/selectors';
import './Website.scss';

import WebsiteConfig from '../../components/Website/WebsiteConfig';
import Theme from '../../components/Website/WebsiteTheme';
import WebsiteFlow from '../../components/Website/WebsiteFlow';
import WebsiteButtons from '../../components/Website/WebsiteButtons';
import WebsiteStandaloneProducts from '../../components/Website/WebsiteStandaloneProducts';
import ErrorMessage from '../../components/ErrorMessage';

function WebsiteContainer(props) {
  const { websiteId } = props.match.params;

  const {
    actions,
    website,
    history,
    match,
    isConfigRoute,
    isButtonsRoute,
    isThemeRoute,
    isFlowRoute,
    isStandaloneProducts,
    dealer,
    error,
  } = props;

  useEffect(() => {
    if (websiteId) {
      // If websiteId is present in URL
      // Let's fetch that Website
      actions.websiteRequest({ websiteId });
    } else {
      // websiteId is null
      // Assume Create mode
      // Reset website in state
      actions.resetWebsite({});
      // Reset tmp website
      actions.updateWebsiteTmpRequest({ tmpWebsite: {} });
    }
  }, [actions, websiteId]);

  const menu = [
    {
      name: 'Config',
      getUrl: (id) => (id ? `/website/${id}/config` : `/website`),
      roles: [
        UserRole.SUPER_ADMIN,
        UserRole.ADMIN,
        UserRole.READ_ONLY_ADMIN,
        UserRole.DEALER_ADMIN,
        UserRole.LIMITED_DEALER_ADMIN,
      ],
    },
  ];

  if (website.id) {
    menu.push({
      name: 'Theme',
      getUrl: (id) => `/website/${id}/theme`,
      roles: [
        UserRole.SUPER_ADMIN,
        UserRole.ADMIN,
        UserRole.READ_ONLY_ADMIN,
        UserRole.DEALER_ADMIN,
        UserRole.LIMITED_DEALER_ADMIN,
      ],
    });
    menu.push({
      name: 'Flow',
      getUrl: (id) => `/website/${id}/flow`,
      roles: [
        UserRole.SUPER_ADMIN,
        UserRole.ADMIN,
        UserRole.READ_ONLY_ADMIN,
        UserRole.DEALER_ADMIN,
        UserRole.LIMITED_DEALER_ADMIN,
      ],
    });
    menu.push({
      name: 'Vehicle Detail',
      getUrl: (id) => `/website/${id}/vdp`,
      roles: [
        UserRole.SUPER_ADMIN,
        UserRole.ADMIN,
        UserRole.READ_ONLY_ADMIN,
        UserRole.DEALER_ADMIN,
        UserRole.LIMITED_DEALER_ADMIN,
      ],
    });
    menu.push({
      name: 'Search Results',
      getUrl: (id) => `/website/${id}/srp`,
      roles: [
        UserRole.SUPER_ADMIN,
        UserRole.ADMIN,
        UserRole.READ_ONLY_ADMIN,
        UserRole.DEALER_ADMIN,
        UserRole.LIMITED_DEALER_ADMIN,
      ],
    });
    menu.push({
      name: 'Standalone',
      getUrl: (id) => `/website/${id}/standalone`,
      roles: [
        UserRole.SUPER_ADMIN,
        UserRole.ADMIN,
        UserRole.READ_ONLY_ADMIN,
        UserRole.DEALER_ADMIN,
        UserRole.LIMITED_DEALER_ADMIN,
      ],
    });
    menu.push({
      name: 'API',
      getUrl: (id) => `/website/${id}/api`,
      roles: [
        UserRole.SUPER_ADMIN,
        UserRole.ADMIN,
        UserRole.READ_ONLY_ADMIN,
        UserRole.DEALER_ADMIN,
        UserRole.LIMITED_DEALER_ADMIN,
      ],
    });
    menu.push({
      name: 'Standalone Products',
      getUrl: (id) => `/website/${id}/standalone-products`,
      roles: [
        UserRole.SUPER_ADMIN,
        UserRole.ADMIN,
        UserRole.READ_ONLY_ADMIN,
        UserRole.DEALER_ADMIN,
        UserRole.LIMITED_DEALER_ADMIN,
      ],
    });
  }

  const resetTmp = () => {
    actions.websiteRequest({ websiteId });
  };

  const renderMenuItem = (item) => {
    const url = item.getUrl(websiteId);
    return (
      <LinkContainer to={url} key={url}>
        <NavItem onClick={resetTmp}>{item.name}</NavItem>
      </LinkContainer>
    );
  };

  const title =
    website.id && website.name
      ? `Modify Website: ${website.name}`
      : `Create Website`;

  // Show the tabs only once the stored website matches the one in the URL. The
  // store keeps the last website viewed until the effect above replaces it.
  const isRouteWebsiteLoaded = (website.id || null) === (websiteId || null);

  return (
    <SidebarContainer title={title} openSubMenu="Inventory">
      <div className="website-page-contianer">
        {error && <ErrorMessage error={error} />}
        <Nav bsStyle="tabs">{menu.map(renderMenuItem)}</Nav>
        {isRouteWebsiteLoaded && (
          <>
            {isThemeRoute && (
              <Theme
                website={{ ...website }}
                history={history}
                currentTab="website"
              />
            )}
            {isFlowRoute && (
              <WebsiteFlow
                website={{ ...website }}
                history={history}
                dealer={dealer}
              />
            )}
            {isConfigRoute && (
              <WebsiteConfig
                website={website}
                isNew={!websiteId}
                history={history}
              />
            )}
            {isButtonsRoute && (
              <WebsiteButtons
                website={website}
                history={history}
                match={match}
              />
            )}
            {isStandaloneProducts && (
              <WebsiteStandaloneProducts website={website} history={history} />
            )}
          </>
        )}
      </div>
    </SidebarContainer>
  );
}

WebsiteContainer.propTypes = {
  website: PropTypes.object,
  actions: PropTypes.object,
  match: PropTypes.object,
  dealer: PropTypes.object,
  isConfigRoute: PropTypes.bool,
  isButtonsRoute: PropTypes.bool,
  isThemeRoute: PropTypes.bool,
  isFlowRoute: PropTypes.bool,
  isStandaloneProducts: PropTypes.bool,
  history: PropTypes.object,
};

const mapStateToProps = (state) => ({
  website: getWebsite(state),
  error: state.websites.error,
  feedviews: state.feedviews,
  dealer: state.dealers.dealer,
  user: state.user.currentUser,
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      ...feedviewsActions,
      ...websitesActions,
    },
    dispatch,
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(WebsiteContainer);
